/**
 * House of Melton — Designer Quick View
 *
 * Opens a modal from the homepage "All Designers Grid" block instead of
 * navigating to the brand collection. Products are rendered server-side by
 * sections/designer-quick-view.liquid (Section Rendering API), so prices,
 * currency, translations and availability always match the storefront.
 *
 * Markup contract (see blocks/all-designers-grid.liquid):
 *   <designer-quick-view>
 *     <a data-dqv-trigger href="/collections/brand-x">…</a>   (one per brand)
 *     <dialog data-dqv-dialog>…</dialog>
 *   </designer-quick-view>
 *
 * Progressive enhancement: if JS fails, or the browser lacks <dialog>, or the
 * shopper Ctrl/Cmd/Shift/middle-clicks, the tile still opens the collection.
 */
if (!customElements.get('designer-quick-view')) {
  class DesignerQuickView extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.lastCartResponse = null;
      this.activeTrigger = null;
      this.requestId = 0;
      this.onTriggerClick = this.onTriggerClick.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-dqv-dialog]');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

      this.titleEl = this.dialog.querySelector('[data-dqv-title]');
      this.countEl = this.dialog.querySelector('[data-dqv-count]');
      this.statusEl = this.dialog.querySelector('[data-dqv-status]');
      this.gridEl = this.dialog.querySelector('[data-dqv-grid]');
      this.bodyEl = this.dialog.querySelector('[data-dqv-body]');
      this.moreWrap = this.dialog.querySelector('[data-dqv-more]');
      this.moreButton = this.dialog.querySelector('[data-dqv-load-more]');
      this.viewAllLink = this.dialog.querySelector('[data-dqv-view-all]');
      this.closeButton = this.dialog.querySelector('[data-dqv-close]');
      this.toast = this.dialog.querySelector('[data-dqv-toast]');
      this.toastMessage = this.dialog.querySelector('[data-dqv-toast-message]');
      this.toastCartLink = this.dialog.querySelector('[data-dqv-toast-cart]');
      this.toastDismiss = this.dialog.querySelector('[data-dqv-toast-dismiss]');

      this.querySelectorAll('[data-dqv-trigger]').forEach((trigger) => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this.dialog.id);
        trigger.addEventListener('click', this.onTriggerClick);
      });

      this.closeButton?.addEventListener('click', () => this.close());

      // Click on the backdrop (the dialog element itself, outside the panel) closes.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });

      // ESC fires `cancel` on a modal dialog; route it through close() for cleanup.
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      this.dialog.addEventListener('close', () => this.onClosed());

      this.moreButton?.addEventListener('click', () => this.loadMore());

      this.gridEl.addEventListener('submit', (event) => {
        const form = event.target.closest('form.dqv-card__form');
        if (!form) return;
        event.preventDefault();
        this.addToCart(form);
      });

      this.toastCartLink?.addEventListener('click', (event) => this.onViewCart(event));
      this.toastDismiss?.addEventListener('click', () => this.hideToast());
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      document.body.classList.remove('overflow-hidden');
    }

    /* ------------------------------------------------------------------ */
    /* Open / close                                                        */
    /* ------------------------------------------------------------------ */

    onTriggerClick(event) {
      // Let modified clicks behave like a normal link (new tab, etc.).
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      this.open(event.currentTarget);
    }

    open(trigger) {
      this.activeTrigger = trigger;
      const title = trigger.dataset.title || '';
      const url = trigger.getAttribute('href');

      this.titleEl.textContent = title;
      this.countEl.textContent = '';
      if (this.viewAllLink) {
        this.viewAllLink.href = url;
        this.viewAllLink.hidden = false;
      }
      this.hideToast();
      this.gridEl.innerHTML = '';
      this.moreWrap.hidden = true;

      if (!this.dialog.open) this.dialog.showModal();
      document.body.classList.add('overflow-hidden');
      this.bodyEl.scrollTop = 0;
      this.closeButton?.focus();

      this.load(url, 1, { replace: true });
    }

    close() {
      if (!this.dialog.open) return;
      this.dialog.classList.add('is-closing');
      const finish = () => {
        this.dialog.classList.remove('is-closing');
        if (this.dialog.open) this.dialog.close();
      };
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) return finish();
      setTimeout(finish, 200);
    }

    onClosed() {
      this.requestId += 1; // ignore in-flight responses
      document.body.classList.remove('overflow-hidden');
      this.hideToast();
      const trigger = this.activeTrigger;
      if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('designer-quick-view:closed', { bubbles: true }));
    }

    /* ------------------------------------------------------------------ */
    /* Loading products                                                    */
    /* ------------------------------------------------------------------ */

    buildUrl(collectionUrl, page) {
      const url = new URL(collectionUrl, window.location.origin);
      url.searchParams.set('section_id', 'designer-quick-view');
      // Storefront availability filter (ignored if the filter is not enabled;
      // the section also skips sold-out products itself).
      url.searchParams.set('filter.v.availability', '1');
      if (page > 1) url.searchParams.set('page', page);
      return url.toString();
    }

    async load(collectionUrl, page, { replace = false } = {}) {
      const requestId = ++this.requestId;
      this.currentCollectionUrl = collectionUrl;
      this.setBusy(true, replace);

      try {
        const fetchUrl = this.buildUrl(collectionUrl, page);
        let html = this.cache.get(fetchUrl);
        if (!html) {
          const response = await fetch(fetchUrl, { credentials: 'same-origin' });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          html = await response.text();
          this.cache.set(fetchUrl, html);
        }
        if (requestId !== this.requestId) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const payload = doc.querySelector('.dqv-payload');
        const items = payload ? Array.from(payload.querySelectorAll('.dqv-card')) : [];

        if (replace) this.gridEl.innerHTML = '';
        items.forEach((item) => this.gridEl.appendChild(document.importNode(item, true)));

        const nextPage = payload?.dataset.nextPage;
        this.nextPage = nextPage ? parseInt(nextPage, 10) : null;
        this.moreWrap.hidden = !this.nextPage;

        const total = this.gridEl.querySelectorAll('.dqv-card').length;
        this.updateCount(total, Boolean(this.nextPage));

        if (total === 0 && this.nextPage) {
          // An entire page was sold out; keep going so the shopper sees stock.
          this.setBusy(false);
          return this.load(collectionUrl, this.nextPage);
        }

        if (total === 0) {
          this.showStatus(this.dataset.emptyText || 'No pieces are currently available from this designer.');
        } else {
          this.showStatus('');
        }

        // After "Load more", move focus to the first newly added product.
        if (!replace && items[0]) {
          this.gridEl
            .querySelector(`[data-product-id="${items[0].dataset.productId}"] .dqv-card__title-link`)
            ?.focus({ preventScroll: false });
        }
      } catch (error) {
        if (requestId !== this.requestId) return;
        console.error('[designer-quick-view]', error);
        this.showStatus(this.dataset.errorText || 'We couldn’t load this designer right now.', true);
      } finally {
        if (requestId === this.requestId) this.setBusy(false);
      }
    }

    loadMore() {
      if (!this.nextPage || !this.currentCollectionUrl) return;
      this.load(this.currentCollectionUrl, this.nextPage);
    }

    setBusy(isBusy, showSkeleton = false) {
      this.dialog.toggleAttribute('aria-busy', isBusy);
      if (this.moreButton) {
        this.moreButton.toggleAttribute('disabled', isBusy);
        this.moreButton.classList.toggle('is-loading', isBusy);
      }
      if (isBusy && showSkeleton) {
        this.showStatus('');
        const count = 8;
        this.gridEl.innerHTML = Array.from({ length: count })
          .map(
            () =>
              '<li class="dqv-card dqv-card--skeleton" aria-hidden="true"><span class="dqv-card__media"></span><span class="dqv-skeleton-line"></span><span class="dqv-skeleton-line dqv-skeleton-line--short"></span></li>'
          )
          .join('');
        this.countEl.textContent = this.dataset.loadingText || 'Loading…';
      }
    }

    updateCount(total, hasMore) {
      const one = this.dataset.countOne || '1 piece available';
      const other = this.dataset.countOther || '[count] pieces available';
      const label = total === 1 ? one : other.replace('[count]', total + (hasMore ? '+' : ''));
      this.countEl.textContent = total > 0 ? label : '';
    }

    showStatus(message, isError = false) {
      if (!this.statusEl) return;
      this.statusEl.hidden = !message;
      this.statusEl.classList.toggle('is-error', isError);
      this.statusEl.innerHTML = '';
      if (!message) return;

      const text = document.createElement('p');
      text.textContent = message;
      this.statusEl.appendChild(text);

      if (isError && this.currentCollectionUrl) {
        const link = document.createElement('a');
        link.href = this.currentCollectionUrl;
        link.className = 'dqv__status-link';
        link.textContent = this.dataset.viewAllText || 'View the full collection';
        this.statusEl.appendChild(link);
      }
    }

    /* ------------------------------------------------------------------ */
    /* Quick add                                                           */
    /* ------------------------------------------------------------------ */

    getCartComponent() {
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    async addToCart(form) {
      const button = form.querySelector('[type="submit"]');
      const labelEl = button?.querySelector('.dqv-card__add-label');
      const errorEl = form.querySelector('.dqv-card__error');
      if (!button || button.getAttribute('aria-disabled') === 'true') return;

      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      const cart = this.getCartComponent();
      const sectionIds = new Set(['cart-icon-bubble']);
      if (cart && typeof cart.getSectionsToRender === 'function') {
        cart.getSectionsToRender().forEach((section) => sectionIds.add(section.id));
      }

      const formData = new FormData(form);
      formData.append('sections', Array.from(sectionIds).join(','));
      formData.append('sections_url', window.location.pathname);

      const addUrl = `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
      let succeeded = false;

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          const message = data.description || data.message || window.cartStrings?.error || 'Unable to add this item.';
          if (errorEl) {
            errorEl.textContent = message;
            errorEl.hidden = false;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'designer-quick-view',
              productVariantId: formData.get('id'),
              errors: data.errors || data.description,
              message: data.message,
            });
          }
          return;
        }

        succeeded = true;
        this.lastCartResponse = data;
        this.renderCartBubble(data);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'designer-quick-view',
            productVariantId: formData.get('id'),
            cartData: data,
          });
        }

        if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');

        if (labelEl) labelEl.textContent = this.dataset.addedText || 'Added';
        button.classList.add('is-added');
        const productTitle = form.closest('.dqv-card')?.querySelector('.dqv-card__title-link')?.textContent.trim();
        this.showToast(productTitle);
      } catch (error) {
        console.error('[designer-quick-view]', error);
        if (errorEl) {
          errorEl.textContent = window.cartStrings?.error || 'Unable to add this item.';
          errorEl.hidden = false;
        }
      } finally {
        button.classList.remove('is-loading');
        if (succeeded) {
          setTimeout(() => {
            button.removeAttribute('aria-disabled');
            button.classList.remove('is-added');
            if (labelEl) labelEl.textContent = button.dataset.defaultLabel || 'Add to cart';
          }, 2500);
        } else {
          button.removeAttribute('aria-disabled');
        }
      }
    }

    renderCartBubble(data) {
      const html = data.sections && data.sections['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (!html || !bubble) return;
      const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (section) bubble.innerHTML = section.innerHTML;
    }

    showToast(productTitle) {
      if (!this.toast) return;
      const template = this.dataset.toastText || '[title] was added to your cart';
      this.toastMessage.textContent = template.replace('[title]', productTitle || '');
      this.toast.hidden = false;
      requestAnimationFrame(() => this.toast.classList.add('is-visible'));
    }

    hideToast() {
      if (!this.toast) return;
      this.toast.classList.remove('is-visible');
      this.toast.hidden = true;
    }

    onViewCart(event) {
      const cart = this.getCartComponent();
      if (!cart || !this.lastCartResponse || typeof cart.renderContents !== 'function') return; // follow link to /cart
      event.preventDefault();

      const response = this.lastCartResponse;
      const trigger = this.activeTrigger;
      this.dialog.addEventListener(
        'close',
        () => {
          setTimeout(() => {
            if (typeof cart.setActiveElement === 'function' && trigger) cart.setActiveElement(trigger);
            cart.renderContents(response);
          });
        },
        { once: true }
      );
      this.close();
    }
  }

  customElements.define('designer-quick-view', DesignerQuickView);
}
